import React, { useId } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../ui";

/** Types */
export type StatusItem = { type: string; definition: string };

type DropdownCardProps = {
  items: StatusItem[];
  /** set to null to start collapsed, or an index (e.g., 0) to start open */
  defaultOpenIndex?: number | null;
  className?: string;
  /** Heading level wrapping each trigger; pick one below the surrounding section heading */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
};

// Render the definition text: paragraphs split on blank lines, "Term: text"
// paragraphs get a bold lead-in.
const formatDefinition = (definition: string) => {
  const paragraphs = definition.split("\n\n");
  const listPattern = /^([A-Z][^:]+):\s*(.+)$/;

  return paragraphs.map((para, index) => {
    const match = para.match(listPattern);
    return (
      <p key={index} className={index > 0 ? "mt-3" : undefined}>
        {match ? (
          <>
            <span className="font-medium text-gray-900">{match[1]}:</span> {match[2]}
          </>
        ) : (
          para
        )}
      </p>
    );
  });
};

/** Accordion card: one row open at a time */
export const DropdownCard: React.FC<DropdownCardProps> = ({
  items,
  defaultOpenIndex = null,
  className,
  headingLevel = 3,
}) => {
  const Heading = `h${headingLevel}` as "h2" | "h3" | "h4" | "h5" | "h6";
  const [openIndex, setOpenIndex] = React.useState<number | null>(defaultOpenIndex);
  // Unique per instance so several accordions on one page don't share element IDs
  const baseId = useId();

  const toggle = (idx: number) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-gray-200 bg-white",
        className
      )}
    >
      <ul className="divide-y divide-gray-100">
        {items.map((item, idx) => {
          const open = openIndex === idx;
          const contentId = `${baseId}-panel-${idx}`;
          const buttonId = `${baseId}-btn-${idx}`;

          return (
            <li key={`${item.type}-${idx}`}>
              <Heading>
                <button
                  id={buttonId}
                  type="button"
                  aria-expanded={open}
                  aria-controls={contentId}
                  className={cn(
                    "flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors sm:px-5",
                    "hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                  )}
                  onClick={() => toggle(idx)}
                >
                  <span className="flex-1 text-sm font-medium text-gray-900">{item.type}</span>
                  <ChevronDown
                    aria-hidden
                    className={cn(
                      "size-4 shrink-0 text-gray-400 transition-transform duration-200",
                      open && "rotate-180 text-gray-600"
                    )}
                  />
                </button>
              </Heading>

              <div
                id={contentId}
                role="region"
                aria-labelledby={buttonId}
                hidden={!open}
                className="px-4 pb-4 text-sm leading-relaxed text-gray-600 sm:px-5"
              >
                {formatDefinition(item.definition)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
